import { html, LitElement, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

import type { SbAuthProvider } from "@/types";
import { genRandSeed } from "@/utils";

import type {
  ProviderButtonContent,
  ProviderButtonIconAlign,
  ProviderButtonSize,
  ProviderButtonVariant,
} from "@/components/renderers/auth/button/button.types";

import type {
  ProviderFieldOverride,
  ProviderFieldProps,
} from "./provider-field.types";

/**
 * A wrapper field, containing and styling auth provider buttons.
 * @element sb-provider-field
 *
 * @part provider-field (rtg-field)
 * @part provider-field-button (sb-provider-button)
 */
@customElement("sb-provider-field")
export class SbProviderField extends LitElement implements ProviderFieldProps {
  private static readonly _ROOT = "provider-field";
  private static readonly _BUTTON = "button";

  /**
   * The stable CSS part names exposed for external targeting.
   */
  static get parts() {
    const root = this._ROOT;
    const button = `${root}-${this._BUTTON}`;

    return { root, button };
  }

  /**
   * A unique value used as a substring in the generated part/child IDs, when
   * the `child-id` prop is not provided.
   */
  @property({ type: String })
  seed = genRandSeed();

  /**
   * The `id` value given to the root child/part and used as the base of other
   * child/part IDs.
   */
  @property({ attribute: "custom-id", type: String })
  customId?: string;

  /**
   * The providers included as provider buttons in this field.
   */
  @property({ type: Array })
  providers: SbAuthProvider[] = [];

  /**
   * The `content` prop passed to each of the provider buttons.
   */
  @property({ type: String })
  content?: ProviderButtonContent;

  /**
   * The `variant` prop passed to each of the provider buttons.
   */
  @property({ type: String })
  variant?: ProviderButtonVariant;

  /**
   * The `size` prop passed to each of the provider buttons.
   */
  @property({ type: String })
  size?: ProviderButtonSize;

  /**
   * The `prompt` prop passed to each of the provider buttons.
   */
  @property({ type: String })
  prompt?: string;

  /**
   * The `icon-align` prop passed to each of the provider buttons.
   */
  @property({ attribute: "icon-align", type: String })
  iconAlign?: ProviderButtonIconAlign;

  /**
   * The `label` prop passed to each of the provider buttons.
   */
  @property({ type: String })
  label?: string;

  /**
   * The `disabled` prop passed to each of the provider buttons.
   */
  @property({ type: Boolean })
  disabled?: boolean;

  /**
   * The `loading` prop passed to each of the provider buttons.
   */
  @property({ type: Boolean })
  loading?: boolean;

  /**
   * The `event` prop passed to each of the provider buttons.
   */
  @property({ type: String })
  event?: string;

  /**
   * A list of provider-specific prop overrides.
   */
  @property({ type: Array })
  overrides: ProviderFieldOverride[] = [];

  /**
   * Overrides default behavior to render into light DOM.
   */
  protected createRenderRoot() {
    return this;
  }

  /**
   * The `id` of the root element. Equal to `customId` when provided, otherwise
   * derived from the component's part name and `seed`.
   */
  get rootId() {
    return this.customId ?? `sb-${SbProviderField.parts.root}-${this.seed}`;
  }

  /**
   * The `id` of the provider button element, derived from `rootId` with the
   * button part name appended.
   */
  get buttonId() {
    return `${this.rootId}-${SbProviderField._BUTTON}`;
  }

  /**
   * The number of columns within the field, given to the root element via its
   * `data-columns` attribute. In hyphenated values (e.g. `"two-three"`), the
   * first part is the number of columns in the first row, and the second part
   * is the number of columns in the second row.
   */
  get columns() {
    const total = this._providers.length;

    if (total < 3) return "one";
    if (total % 3 === 0) return "three";
    if (total === 5) return "two-three";
    if (total === 7) return "three-four";

    return "four";
  }

  /**
   * The `providers` array without any duplicates.
   */
  private get _providers() {
    return [...new Set(this.providers)];
  }

  /**
   * The `overrides` array without any duplicate `provider` entries (i.e. no
   * two override items have the same `provider` value).
   */
  private get _overrides() {
    return this.overrides.filter(
      (override, index, self) =>
        index === self.findIndex((o) => o.provider === override.provider),
    );
  }

  /**
   * Returns the template for the button part, specifically an
   * `sb-provider-button` element with props originating from `overrides`
   * if given, otherwise the general prop given to this field component.
   */
  private _renderButton(props: ProviderFieldOverride) {
    const event = `signin_with_oauth(${props.provider})`;
    const content =
      props.content ??
      this.content ??
      (this._providers.length > 2 ? "icon" : undefined);

    return html`
      <sb-provider-button
        id="${this.buttonId}"
        part="${SbProviderField.parts.button}"
        seed="${props.seed ?? this.seed}"
        custom-id="${props.customId ?? nothing}"
        provider="${props.provider}"
        variant="${props.variant ?? this.variant ?? nothing}"
        size="${props.size ?? this.size ?? nothing}"
        content="${content ?? nothing}"
        icon-align="${props.iconAlign ?? this.iconAlign ?? nothing}"
        prompt="${props.prompt ?? this.prompt ?? nothing}"
        label="${props.label ?? this.label ?? nothing}"
        event="${props.event ?? this.event ?? event ?? nothing}"
        ?disabled="${props.disabled || this.disabled}"
        ?loading="${props.loading || this.loading}"
      ></sb-provider-button>
    `;
  }

  render() {
    const overrides = this._overrides;

    return html`
      <rtg-field
        id="${this.rootId}"
        part="${SbProviderField.parts.root}"
        data-columns="${this.columns}"
      >
        ${this._providers.map((provider) =>
          this._renderButton(
            overrides.find((o) => o.provider === provider) ?? { provider },
          ),
        )}
      </rtg-field>
    `;
  }
}
